import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {SectionView} from '../../ui_canvas_engine/SectionView'
import {CardView} from '../../ui_canvas_engine/CardView'
import {UIGameConstants} from '../../ui_game/UIGameConstants'
import {CardDataController} from '@colonist/game-card-types'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {TranslationObject, getNestedTranslation} from '@colonist/i18n'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {TextView} from '../../ui_canvas_engine/TextView'
import {Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {UIGameTradeBeginnerCardAnimationsController} from '../UIGameTradeBeginnerCardAnimationsController'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {GameFont} from '../../GameFont'
import {GameColor} from '../../GameColor'
import {UIGameFlickerAnimation} from '../../ui_canvas_engine/animation/UIGameFlickerAnimation'
import {bringToFront} from '../../ui_utils/PixiUtils'
import {CardEnum} from '@colonist/game-states'

interface CardDimensions {
    cardH: number
    cardW: number
    cardY: number
    cardOuterMargin: number
    cardsAvailableWidth: number
    cardAverageInnerMargin: number
    cardInnerMargin: number
}

export class UIGameTradeWantedCardSelectorView extends SectionBgView {
    private readonly uiGameManager: UIGameManager
    private readonly tradeRatioHelpIconPercentageSize = 0.4
    private readonly cardViews: CardView[] = []
    private readonly wantedCardSelector: SectionView
    private bankTradeRatioHelpContent: SectionBgView

    beginnerCardAnimationController: UIGameTradeBeginnerCardAnimationsController

    bankTradeModeIcon: SpriteView

    private readonly BANK_TRADE_MODE_ICON_OFF_ALPHA = 0.5
    private readonly BANK_TRADE_MODE_ICON_ON_ALPHA = 1

    private readonly cardHeight: number
    private readonly containerWidth: number

    get isBankTradeRatioVisible(): boolean {
        return this.bankTradeRatioHelpContent.visible
    }

    constructor(uiGameManager: UIGameManager, x: number, y: number, width: number, height: number, container: PIXI.Container) {
        super(x, y, width, height, container)

        this.uiGameManager = uiGameManager

        this.drawRoundedBg()

        this.wantedCardSelector = new SectionView(0, 0, this.width, this.height, this)
        this.createWantedCardSelector()
        this.createBankTradeMode(this.wantedCardSelector)

        this.cardHeight = this.height
        this.containerWidth = this.wantedCardSelector.width
    }

    createCounterOffer(receivingResources: CardEnum[]) {
        receivingResources.forEach((cardEnum: CardEnum) => {
            this.uiGameManager.tradeController.proposalView.wantedCardPlayerInfo.cardContainer.addNewCardOfType(cardEnum)
        })
    }

    toggleBankTradeIcon(isBankTrade: boolean) {
        this.bankTradeModeIcon.alpha = isBankTrade ? this.BANK_TRADE_MODE_ICON_ON_ALPHA : this.BANK_TRADE_MODE_ICON_OFF_ALPHA
        if(UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI) this.bankTradeRatioHelpContent.visible = isBankTrade
    }

    private createBankTradeMode(wantedCardSelector: SectionView) {
        this.bankTradeModeIcon = this.createBankTradeModeIcon(wantedCardSelector)
        this.bankTradeModeIcon.setClickAction(() => {
            this.uiGameManager.tradeController.toggleBankTradeMode(!this.uiGameManager.tradeController.inBankTradeMode)
        })

        this.createBankTradeRatioHelp(wantedCardSelector, this.bankTradeModeIcon)
    }

    private createBankTradeRatioHelp(wantedCardSelector: SectionView, hoverTarget: SpriteView) {
        this.bankTradeRatioHelpContent = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI ? this.createBankTradeRatioHelpContentForMobile(wantedCardSelector) : this.createBankTradeRatioHelpContent(wantedCardSelector)

        hoverTarget.setHoverAction(() => {
            this.bankTradeRatioHelpContent.visible = true
            bringToFront(this)
        }, () => {
            this.bankTradeRatioHelpContent.visible = false
        })

        this.bankTradeRatioHelpContent.visible = false
    }

    private calculateCardDimensions(content: SectionView, activeCards: CardEnum[]): CardDimensions {
        const {margin} = this.uiGameManager.canvasController
        const cardH = this.cardHeight * 0.8
        const cardW = cardH / UIGameConstants.CARD_RATIO
        const cardY = (this.cardHeight - cardH) / 2
        const cardOuterMargin = this.cardHeight * 0.1

        const helpIconSize = this.cardHeight * this.tradeRatioHelpIconPercentageSize
        const cardsAvailableWidth = this.containerWidth - cardOuterMargin - helpIconSize
        const cardAverageInnerMargin = (cardsAvailableWidth - cardW * activeCards.length) / (activeCards.length - 1)
        const cardInnerMargin = Math.min(cardAverageInnerMargin, margin)

        return {cardH: cardH, cardW: cardW, cardY: cardY, cardOuterMargin: cardOuterMargin, cardsAvailableWidth: cardsAvailableWidth, cardAverageInnerMargin: cardAverageInnerMargin, cardInnerMargin: cardInnerMargin}
    }

    private createCardView(index: number, activeCard: CardEnum, dimensions: CardDimensions, content: SectionView): CardView {
        const cardX = dimensions.cardW * index + dimensions.cardInnerMargin * index + dimensions.cardOuterMargin
        const cardView = new CardView(this.uiGameManager, 0, cardX, dimensions.cardY, dimensions.cardW, dimensions.cardH, activeCard, content, true)
        cardView.hideBadge()
        cardView.view.setClickAction(() => {
            const proposalWantedAlreadyContainsAnyResourceCard = this.uiGameManager.tradeController.proposalView.wantedCardPlayerInfo.containsAnyResourceCard()
            if(activeCard == CardEnum.AnyResource && proposalWantedAlreadyContainsAnyResourceCard) return
            this.uiGameManager.tradeController.proposalView.wantedCardPlayerInfo.cardContainer.addNewCardOfType(activeCard)
            cardView.y = dimensions.cardY
        })
        return cardView
    }

    private createWantedCardSelector(isAnyResourceCardEnabled: boolean = false) {
        const activeCards = CardDataController.getCardEnumsOfTypes(this.uiGameManager.gameState.gameModeData.activeResourceCardTypes)
        if(isAnyResourceCardEnabled) activeCards.push(CardEnum.AnyResource)

        const dimensions = this.calculateCardDimensions(this.wantedCardSelector, activeCards)
        for(const [index, activeCard] of activeCards.entries()) {
            const cardView = this.createCardView(index, activeCard, dimensions, this.wantedCardSelector)
            this.cardViews.push(cardView)
        }
        this.beginnerCardAnimationController = new UIGameTradeBeginnerCardAnimationsController(this.uiGameManager, this.cardViews)
    }

    resetCards(isAnyResourceCardEnabled: boolean = false) {
        const cardsAnimating = this.beginnerCardAnimationController.cardEnumsForBeginnerCardAnimation
        this.beginnerCardAnimationController.stopBeginnerAnimation()
        this.removeAllCardViews()
        this.createWantedCardSelector(isAnyResourceCardEnabled)
        this.beginnerCardAnimationController.startBeginnerAnimation(cardsAnimating)
    }

    private removeAllCardViews() {
        this.cardViews.forEach((cardView) => cardView.destroy())
        this.cardViews.splice(0, this.cardViews.length)
    }

    updateBankTradeRatioHelpContent(isVisible: boolean = this.isBankTradeRatioVisible) {
        const container = this.bankTradeRatioHelpContent.parent as SectionView
        if(this.bankTradeRatioHelpContent != undefined) this.bankTradeRatioHelpContent.destroy()

        this.bankTradeRatioHelpContent = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI ? this.createBankTradeRatioHelpContentForMobile(container) : this.createBankTradeRatioHelpContent(container)
        this.bankTradeRatioHelpContent.visible = isVisible
    }

    private createBankTradeModeIcon(bankTradeWantedCardSelectors: SectionView): SpriteView {
        const ratioIconSize = bankTradeWantedCardSelectors.height * this.tradeRatioHelpIconPercentageSize
        const ratioIconX = bankTradeWantedCardSelectors.width - ratioIconSize
        const ratioIconY = bankTradeWantedCardSelectors.height * 0.5
        const ratioIconTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bank)
        const ratioIcon = new SpriteView(ratioIconX, ratioIconY, ratioIconSize, ratioIconSize, bankTradeWantedCardSelectors, ratioIconTexture)
        ratioIcon.anchor.set(0, 0.5)
        ratioIcon.alpha = this.BANK_TRADE_MODE_ICON_OFF_ALPHA

        return ratioIcon
    }

    private createBankTradeRatioHelpIcon(bankTradeWantedCardSelectors: SectionView): SpriteView {
        const ratioIconSize = bankTradeWantedCardSelectors.height * this.tradeRatioHelpIconPercentageSize
        const ratioIconX = bankTradeWantedCardSelectors.width - ratioIconSize
        const ratioIconY = bankTradeWantedCardSelectors.height * 0.5
        const ratioIconTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_info)
        const ratioIcon = new SpriteView(ratioIconX, ratioIconY, ratioIconSize, ratioIconSize, bankTradeWantedCardSelectors, ratioIconTexture)
        ratioIcon.anchor.set(0, 0.5)

        return ratioIcon
    }

    private createBankTradeRatioHelpContent(bankTradeWantedCardSelectors: SectionView): SectionBgView {
        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        const maxRowsPerColumns = 5
        const numberOfColumns = Math.ceil(bankTradeRatios.length / maxRowsPerColumns)

        const {itemSize, margin} = this.uiGameManager.canvasController
        const x = this.width
        const y = -margin
        const width = itemSize * numberOfColumns
        const height = itemSize * 2 - margin * 2
        const container = new SectionBgView(x, y, width, height, bankTradeWantedCardSelectors)
        container.drawRoundedBg()

        const containerHeightExcludingMargins = container.height - (maxRowsPerColumns + 1) * margin
        const containerColumnCenterX = container.width / numberOfColumns / 2
        const cardHeight = containerHeightExcludingMargins / maxRowsPerColumns
        const cardWidth = cardHeight / UIGameConstants.CARD_RATIO
        const columnWidth = container.width / numberOfColumns
        bankTradeRatios.forEach((ratio, index) => {
            if(ratio.tradeRatio == undefined) return
            const rowIndex = index % maxRowsPerColumns
            const columnIndex = Math.floor(index / maxRowsPerColumns)
            const viewX = columnIndex * columnWidth + containerColumnCenterX

            const cardX = viewX - margin - cardWidth
            const cardY = margin + rowIndex * cardHeight + rowIndex * margin
            const cardView = new CardView(this.uiGameManager, 0, cardX, cardY, cardWidth, cardHeight, ratio.resource, container)
            cardView.hideBadge()

            const text = UIGameTradeWantedCardSelectorView.getBankTradeRatioText(ratio.tradeRatio)
            const textX = viewX + margin
            const ratioView = new TextView(text, textX, cardY, 100, cardHeight, container)

            const viewTooltip = getNestedTranslation({key: 'strings:game.tooltips.tradeRatios.body', options: {tradeRatio: ratio.tradeRatio}}, {
                cardName: cardView.cardData.tipTitle,
            })
            cardView.view.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [viewTooltip])
            ratioView.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [viewTooltip])
        })

        return container
    }

    private createBankTradeRatioHelpContentForMobile(bankTradeWantedCardSelectors: SectionView): SectionBgView {
        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        const maxRowsPerColumns = bankTradeRatios.length > 5 ? 2 : 1
        const numberOfColumns = 5

        const {itemSize, margin, appW} = this.uiGameManager.canvasController
        const width = Math.min(itemSize * numberOfColumns, appW - 2 * margin)
        const height = itemSize * 0.6 * maxRowsPerColumns
        const x = -margin
        const y = -height - margin
        const container = new SectionBgView(x, y, width, height, bankTradeWantedCardSelectors)
        container.drawBg(GameColor.getColorHexValue(GameColor.Color.Gray0), 1)

        const containerHeightExcludingMargins = container.height - (maxRowsPerColumns + 1) * margin
        const containerColumnCenterX = container.width / numberOfColumns / 2
        const cardHeight = containerHeightExcludingMargins / maxRowsPerColumns
        const cardWidth = cardHeight / UIGameConstants.CARD_RATIO
        const columnWidth = container.width / numberOfColumns
        bankTradeRatios.forEach((ratio, index) => {
            if(ratio.tradeRatio == undefined) return
            const rowIndex = Math.floor(index / numberOfColumns)
            const totalEmptyItem = rowIndex === maxRowsPerColumns - 1 ? numberOfColumns * maxRowsPerColumns - bankTradeRatios.length : 0
            const columnIndex = totalEmptyItem + index % numberOfColumns
            const viewX = columnIndex * columnWidth + containerColumnCenterX

            const cardX = viewX - margin - cardWidth
            const cardY = margin + rowIndex * cardHeight + rowIndex * margin
            const cardView = new CardView(this.uiGameManager, 0, cardX, cardY, cardWidth, cardHeight, ratio.resource, container)
            cardView.hideBadge()

            const text = UIGameTradeWantedCardSelectorView.getBankTradeRatioText(ratio.tradeRatio)
            const textX = viewX + margin
            new TextView(text, textX, cardY, 100, cardHeight, container, GameFont.Size.Small, GameColor.getColor(GameColor.Color.White))
        })
        container.x = appW - container.width - 2 * margin - itemSize
        return container
    }

    private static getBankTradeRatioText(ratio: number): TranslationObject {
        return {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: `${ratio}:1`,
            },
        }
    }

    flickerAllCardsOfTypes(cardEnums: CardEnum[]) {
        const cardViews = this.cardViews.filter((cardView) => cardEnums.includes(cardView.cardData.cardEnum))
        for(const cardView of cardViews) {
            cardView.setAnimation(new UIGameFlickerAnimation(cardView.view))
        }
    }

    stopAnimation() {
        for(const cardView of this.cardViews) {
            cardView.stopAnimation()
        }
    }

    enableAllCards() {
        this.cardViews.forEach((cardView) => cardView.fadeInCard())
    }

    disableCards(cardEnums: CardEnum[]) {
        const cardViews = this.cardViews.filter((cardView) => cardEnums.includes(cardView.cardData.cardEnum))
        for(const cardView of cardViews) {
            cardView.fadeOutCard()
        }
    }

    removeCardsClickAction(cardEnums: CardEnum[]) {
        this.disableCards(cardEnums)
        const cardViews = this.cardViews.filter((cardView) => cardEnums.includes(cardView.cardData.cardEnum))
        for(const card of cardViews) card.view.removeClickAction()
    }

    hideBankTradeModeIcon() {
        const bankTradeModeIconShown = this.bankTradeModeIcon.parent != undefined
        if(bankTradeModeIconShown) this.wantedCardSelector.removeChild(this.bankTradeModeIcon)
    }

    showBankTradeModeIcon() {
        const bankTradeModeIconHidden = this.bankTradeModeIcon.parent == undefined
        if(bankTradeModeIconHidden) this.wantedCardSelector.addChild(this.bankTradeModeIcon)
    }
}
